import SearchIcon from '@mui/icons-material/Search';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    LinearProgress,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import srtSearchServiceInstance from '../../services/SrtSearchService';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const POLL_INTERVAL_MS = 2000;

const formatTime = (seconds) => {
    if (seconds == null || isNaN(seconds)) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
};

const SnippetText = ({ snippet }) => {
    if (!snippet) return null;
    const parts = snippet.split(/\*\*(.*?)\*\*/g);
    return (
        <span>
            {parts.map((part, i) =>
                i % 2 === 1 ? (
                    <strong key={i} style={{ color: 'inherit' }}>
                        {part}
                    </strong>
                ) : (
                    part
                ),
            )}
        </span>
    );
};

const SrtSearchDialog = ({ open, onClose, currentPath, onResultClick }) => {
  useTrackUsage('SrtSearchDialog', 'dialog', 'folder-browser', open);
    const [query, setQuery] = useState('');
    const [wholeWord, setWholeWord] = useState(true);
    const [status, setStatus] = useState('idle'); // idle | loading | processing | completed | failed
    const [message, setMessage] = useState('');
    const [results, setResults] = useState([]);
    const [error, setError] = useState('');
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const pollTimerRef = useRef(null);

    const stopPolling = () => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    };

    useEffect(() => {
        if (open) {
            setQuery('');
            setWholeWord(true);
            setStatus('idle');
            setMessage('');
            setResults([]);
            setError('');
            setSelectedLanguages([]);
        } else {
            stopPolling();
        }
        return () => stopPolling();
    }, [open]);

    const handleClose = () => {
        stopPolling();
        onClose();
    };

    const handleSearch = async () => {
        if (!query.trim()) return;
        stopPolling();
        setStatus('loading');
        setMessage('');
        setResults([]);
        setError('');
        setSelectedLanguages([]);

        try {
            const { jobId } = await srtSearchServiceInstance.startSearch(currentPath, query.trim(), wholeWord);
            setStatus('processing');

            pollTimerRef.current = setInterval(async () => {
                try {
                    const data = await srtSearchServiceInstance.getStatus(jobId);
                    if (data.message) setMessage(data.message);

                    if (data.status === 'COMPLETED') {
                        stopPolling();
                        setResults(data.results || []);
                        setStatus('completed');
                    } else if (data.status === 'FAILED') {
                        stopPolling();
                        setError(data.error || 'Search failed on the server.');
                        setStatus('failed');
                    }
                } catch (pollErr) {
                    stopPolling();
                    setError(pollErr.message || 'Error polling search status.');
                    setStatus('failed');
                }
            }, POLL_INTERVAL_MS);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to start search.');
            setStatus('failed');
        }
    };

    const availableLanguages = [...new Set(results.map((r) => r.languageName))].sort();

    const filteredResults = selectedLanguages.length === 0
        ? results
        : results.filter((r) => selectedLanguages.includes(r.languageName));

    // Group: { videoId -> { videoName, languages: { langName -> [result] } } }
    const grouped = filteredResults.reduce((acc, r) => {
        if (!acc[r.videoId]) acc[r.videoId] = { videoName: r.videoName, languages: {} };
        if (!acc[r.videoId].languages[r.languageName]) acc[r.videoId].languages[r.languageName] = [];
        acc[r.videoId].languages[r.languageName].push(r);
        return acc;
    }, {});

    const isSearching = status === 'loading' || status === 'processing';

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SubtitlesIcon color="primary" />
                    <Typography variant="h6">Search in SRT Content</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {currentPath}
                </Typography>
            </DialogTitle>

            <DialogContent>
                {/* Search controls */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                    <TextField
                        label="Search term"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !isSearching && handleSearch()}
                        size="small"
                        fullWidth
                        disabled={isSearching}
                        autoFocus
                        sx={{ '& .MuiInputBase-root': { height: 40 } }}
                    />
                    <ToggleButtonGroup
                        value={wholeWord ? 'whole' : 'contains'}
                        exclusive
                        onChange={(_, v) => { if (v != null) setWholeWord(v === 'whole'); }}
                        size="small"
                        disabled={isSearching}
                        sx={{ height: 40, flexShrink: 0 }}
                    >
                        <Tooltip title="Match full word only">
                            <ToggleButton value="whole" sx={{ height: 40, px: 1.5, fontSize: '0.8rem' }}>Whole Word</ToggleButton>
                        </Tooltip>
                        <Tooltip title="Match anywhere in text">
                            <ToggleButton value="contains" sx={{ height: 40, px: 1.5, fontSize: '0.8rem' }}>Contains</ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        disabled={isSearching || !query.trim()}
                        sx={{ whiteSpace: 'nowrap', height: 40, flexShrink: 0 }}
                    >
                        Search
                    </Button>
                </Box>

                {/* Progress */}
                {isSearching && (
                    <Box sx={{ mb: 2 }}>
                        <LinearProgress />
                        {message && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {message}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Error */}
                {status === 'failed' && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Language filter */}
                {status === 'completed' && availableLanguages.length > 1 && (
                    <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
                        <InputLabel>Filter by language</InputLabel>
                        <Select
                            multiple
                            value={selectedLanguages}
                            onChange={(e) => setSelectedLanguages(e.target.value)}
                            input={<OutlinedInput label="Filter by language" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((v) => <Chip key={v} label={v} size="small" />)}
                                </Box>
                            )}
                        >
                            {availableLanguages.map((lang) => (
                                <MenuItem key={lang} value={lang}>
                                    <ListItemText primary={lang} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Results */}
                {status === 'completed' && (
                    filteredResults.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No matches found for &ldquo;{query}&rdquo;
                        </Typography>
                    ) : (
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                {message || `Found ${filteredResults.length} match(es)`}
                            </Typography>
                            {Object.entries(grouped).map(([videoId, { videoName, languages }]) => (
                                <Box key={videoId} sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        {videoName}
                                    </Typography>
                                    {Object.entries(languages).map(([langName, cues]) => (
                                        <Box key={langName} sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                                            <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'action.hover' }}>
                                                <Typography variant="caption" fontWeight="medium">{langName}</Typography>
                                            </Box>
                                            <Divider />
                                            {cues.map((cue, idx) => (
                                                <Box
                                                    key={idx}
                                                    onClick={() => onResultClick && onResultClick({ videoId, videoName, ...cue })}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        px: 1.5,
                                                        py: 0.75,
                                                        cursor: onResultClick ? 'pointer' : 'default',
                                                        '&:hover': onResultClick ? { bgcolor: 'action.hover' } : {},
                                                        borderBottom: idx < cues.length - 1 ? '1px solid' : 'none',
                                                        borderColor: 'divider',
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                        [{formatTime(cue.startTime)}]
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <SnippetText snippet={cue.matchSnippet || cue.text} />
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    )
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default SrtSearchDialog;
